import { useCallback, useEffect, useMemo, useState } from "react";
import { apiEndpoint } from "../config";
import { ErrorLike } from "../types";
import { useInterval } from "../utils";
import { useGeraet } from "./hooks";
import { locationRounder, useRoundedLocation } from "./location";

// update weather data every 5 minutes
const pollInterval = 5 * 60 * 1000;

export type WetterdatenData = {
  ort: string;
  temperatur: number;
  feuchtigkeit: number;
  luftdruck: number;
  bewoelkung: number;
  windrichtung: "N" | "NO" | "O" | "SO" | "S" | "SW" | "W" | "NW";
  windgeschwindigkeit: number;
  boeen?: number;
  iconUrl?: string;
  beschreibung?: string;
  sonnenaufgang: Date;
  sonnenuntergang: Date;
  stand: Date;
};

export type Wetterdaten =
  | { status: "loading" }
  | { status: "error"; error: Error }
  | { status: "unknown" }
  | ({
      status: "loaded";
    } & WetterdatenData);

const precision = 2;

export function useWetter() {
  const { homebase } = useGeraet();
  const location = useRoundedLocation(precision);
  const url = useMemo(() => {
    const reference =
      location || (homebase && locationRounder(precision)(homebase));
    return (
      reference &&
      `${apiEndpoint}/wetter?lat=${reference.latitude}&lng=${reference.longitude}`
    );
  }, [homebase, location]);
  const { loading, error, data } = useGet(url);

  const context: Wetterdaten = useMemo(() => {
    if (loading) return { status: "loading" };
    if (error) return { status: "error", error };
    if (!data) return { status: "unknown" };
    return {
      ...data,
      status: "loaded",
      sonnenaufgang: new Date(data.sonnenaufgang),
      sonnenuntergang: new Date(data.sonnenuntergang),
      stand: new Date(data.stand),
    };
  }, [loading, error, data]);

  return context;
}

function useGet(url?: string) {
  const [state, setState] = useState<{
    loading: boolean;
    error?: ErrorLike;
    data?: any;
  }>({ loading: false });

  const loadData = useCallback(async () => {
    if (url) {
      try {
        setState((s) => ({ ...s, loading: true }));
        const response = await fetch(url, {
          headers: { accept: "application/json" },
        });
        if (!response.ok)
          throw new Error(`Fehler: ${response.status} ${response.statusText}`);
        const data = await response.json();
        setState({ loading: false, data });
      } catch (error: any) {
        setState((s) => ({ ...s, loading: false, error }));
      }
    }
  }, [url]);

  useInterval(loadData, pollInterval);

  useEffect(() => {
    loadData();
  }, [loadData]);

  return state;
}
